import { useState } from 'react';
import { useEffect } from 'react';

import { AdminPermissions } from 'constants/admin-permissions';
import { PartnersHuman } from 'constants/partners';
import { CC_STATUS_ICON_DISPLAY } from 'constants/statuses';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useAccessControl } from 'hooks/access-control';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import toast from 'react-hot-toast';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { debounce } from 'utils/requestHelpers';

import { IconComponent } from 'components/Icon/IconComponent';
import { Table } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { selectAuthentication } from 'api/authentication-slice';
import { useLazyGetAllCandidatesQuery } from 'api/candidates-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { useGetAllCompaniesListQuery } from 'api/company-api';

import styles from './AdminCandidates.module.scss';

const AdminCandidates = () => {
  const authentication = useSelector(selectAuthentication);
  const hasPermission = useAccessControl(authentication.admin_permissions);

  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const [getAllCandidates] = useLazyGetAllCandidatesQuery();
  const { data: companiesData } = useGetAllCompaniesListQuery();
  const { data: checkTypes } = useGetCheckTypesQuery();

  useDocumentTitle('Candidates');

  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  useEffect(() => {
    if (!companiesData) return;

    setCompaniesOptions([
      { name: 'All', value: 'all' },
      ...companiesData.result.map(c => ({ name: c.name, value: c.id })),
    ]);
  }, [companiesData]);

  const fetchCandidates = debounce(async (params = {}) => {
    try {
      setLoading(true);
      const { result } = await getAllCandidates(params).unwrap();
      setPagination(result.pagination);
      setCandidates(result.candidates);
      setLoading(false);
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error fetching candidates"
            message={String(
              error?.data?.message || error?.data?.error || error?.message || 'Unknown error',
            )}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  });

  return (
    <AdminPage loading={loading} title="Candidates" allowedRole={AdminPermissions.ListCandidates}>
      {checkTypes?.length > 0 && (
        <Table.Context
          onQueryUpdate={fetchCandidates}
          defaultQuery={{
            page: { value: 1 },
            status: { value: 'all' },
            search: { value: '' },
            company: { value: 'all' },
          }}>
          <Table.FloatingFilters className="surface-white u-margin--large">
            <Table.FloatingFiltersLeft>
              <Table.Filter title="Company" filterName="company" options={companiesOptions} />
              <Table.Filter
                title="Status"
                filterName="status"
                options={[
                  { name: 'All', value: 'all' },
                  { name: 'In-Progress', value: 'in-progress' },
                  { name: 'Completed', value: 'completed' },
                  { name: 'Deleted', value: 'deleted' },
                ]}
              />
            </Table.FloatingFiltersLeft>
          </Table.FloatingFilters>
          <Table.TableContent className="u-margin--large">
            <Table.Filters>
              <Table.Search placeholder="Search for a candidate..." />
            </Table.Filters>
            <Table className="u-margin--large">
              <Table.Head>
                <Table.Row>
                  <Table.Header>ID</Table.Header>
                  <Table.Header>Name</Table.Header>
                  <Table.Header>Company</Table.Header>
                  <Table.Header>Email</Table.Header>
                  <Table.Header>Created at</Table.Header>
                  <Table.Header>Checks</Table.Header>
                  <Table.Header>Partner</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {candidates.map(candidate => (
                  <Table.Row key={candidate.id}>
                    <Table.Col>
                      {hasPermission(AdminPermissions.ProcessCandidateChecks) ? (
                        <Link to={`/admin/candidates/${candidate.id}`} className="u-link dark">
                          {candidate.id}
                        </Link>
                      ) : (
                        <span>{candidate.id}</span>
                      )}
                    </Table.Col>
                    <Table.Col>{`${candidate.first_name} ${candidate.last_name}`}</Table.Col>
                    <Table.Col>{candidate.company_name ?? '—'}</Table.Col>
                    <Table.Col>{candidate.email}</Table.Col>
                    <Table.Col>
                      <Moment format="DD/MM/YYYY">{candidate.created_at}</Moment>
                    </Table.Col>
                    <Table.Col>
                      <div className={styles.checks}>
                        {candidate.checks &&
                          JSON.parse(candidate.checks).map(check => {
                            const checkType = checkTypes?.find(
                              checkType => checkType.slug === check.type,
                            );
                            if (!checkType) return;
                            return (
                              <IconComponent
                                tip={checkType.title}
                                noBackground
                                size="small"
                                type={CC_STATUS_ICON_DISPLAY[check.status].color}
                                icon={getCheckTypeIcon(checkType?.slug)}
                              />
                            );
                          })}
                      </div>
                    </Table.Col>
                    <Table.Col>{PartnersHuman[candidate.partner]}</Table.Col>
                  </Table.Row>
                ))}
              </Table.Body>
              <Table.Pagination {...pagination} />
            </Table>
          </Table.TableContent>
        </Table.Context>
      )}
    </AdminPage>
  );
};

export default AdminCandidates;
